import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  LegacyCard as Card,
  Tabs,
  Link,
  ResourceItem,
  Layout,
  InlineStack,
  BlockStack,
  FormLayout,
  Select,
  TextField,
  Text,
} from "@shopify/polaris";
import { useQuery } from "@apollo/client";

// import helpers and constant
import { baseHelper, errorHelper } from "lib/helpers";
import constant from "lib/constant/constant";
import { Banner, ResourceList } from "lib/components";

// import hoc
import { withErrorBoundary } from "lib/hoc";

// import context
import { PrivateContext } from "lib/context";

// import gql
import { GET_SESSION_LIST } from "app/userManagement/apollo/queries";
import { SESSION_LIST } from "app/userManagement/apollo/subscriptions";

// import sub feature
import { useLocation, useNavigate } from "react-router-dom";
import SessionFooter from "./subFeature/sessionFooter";

const AdminSessionList = () => {
  const { cms } = useContext(PrivateContext);
  const location = useLocation();
  const navigate = useNavigate();
  const queryParam = baseHelper.getQueryParams(location.search);
  const prevFilter = baseHelper.queryParamsFromLocation(location);
  const [selectedFilter, setSelectedFilter] = useState("");
  const [taggedWith, setTaggedWith] = useState(prevFilter.list_search || null);
  const [currentPage, setCurrentPage] = useState(parseInt(prevFilter.page, 10) || 1);
  const [selectedLimit, setSelectedLimit] = useState(parseInt(prevFilter.perPage, 10) || 10);
  const [dataToFetch, setDataToFetch] = useState({
    search: prevFilter.search,
    list_filter: prevFilter.list_filter,
    list_search: prevFilter.list_search,
    filter: prevFilter.filter,
    perPage: selectedLimit,
    page: currentPage,
  });
  const [search, setSearch] = useState(queryParam.search || "");
  const [tabSelected, setTabSelected] = useState(parseInt(queryParam.tab, 10) || 0);
  const [totalUSerList, setTotalUserList] = useState(0);
  const [userList, setUserList] = useState([]);
  const [banner, setBanner] = useState({
    isOpen: false,
    status: "",
    title: "",
  });

  const options = [
    { label: "10", value: 10 },
    { label: "20", value: 20 },
    { label: "50", value: 50 },
    { label: "100", value: 100 },
  ];

  const { data: sessionList, loading, subscribeToMore, error } = useQuery(GET_SESSION_LIST, {
    variables: {
      input: {
        search: dataToFetch.search,
        list_filter: dataToFetch.list_filter,
        list_search: dataToFetch.list_search,
        filter: dataToFetch.filter,
        page: parseInt(dataToFetch.page, 10),
        perPage: parseInt(dataToFetch.perPage, 10),
      },
    },
  });

  useEffect(() => {
    const appliedFilterObj = { ...dataToFetch };
    if (Object.keys(appliedFilterObj).length > 0) {
      const tab =
        (Object.prototype.hasOwnProperty.call(appliedFilterObj, cms("label.tabKey")) && appliedFilterObj.tabKey) || 0;
      const limitPerPage =
        (Object.prototype.hasOwnProperty.call(appliedFilterObj, cms("label.perPage")) && appliedFilterObj.perPage) ||
        10;
      const Page =
        (Object.prototype.hasOwnProperty.call(appliedFilterObj, cms("label.page")) && appliedFilterObj.page) || 1;
      setCurrentPage(parseInt(Page, 10));
      setSelectedLimit(parseInt(limitPerPage, 10));
      setTabSelected(parseInt(tab, 10));
    }
  }, [dataToFetch, cms]);

  useEffect(() => {
    if (sessionList) {
      const responseData = baseHelper.getResponseData(sessionList, constant.gql.GET_SESSION_LIST);
      const responseError = baseHelper.getResponseError(sessionList, constant.gql.GET_SESSION_LIST);
      if (responseError) {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        return;
      }
      if (responseData) {
        const { activity: activityData = [], count: totalCount = 0 } = responseData;
        setUserList(activityData);
        setTotalUserList(totalCount);
      }
    }
    if (error) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(error) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, sessionList, errorHelper]);

  // Subscription for User Session List.
  useEffect(() => {
    subscribeToMore({
      document: SESSION_LIST,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;
        const newSessionData = subscriptionData.data.sessionList.data.sessionList;
        const prevSessionList = prev.getSessionList.data;
        const usersListData = [newSessionData, ...prevSessionList.activity];
        const totalCount = parseInt(prevSessionList.count, 10) + 1;
        return [setUserList(usersListData), setTotalUserList(totalCount)];
      },
    });
  }, [subscribeToMore]);

  const tabs = [
    {
      id: "all",
      content: cms("label.tab.all"),
      accessibilityLabel: "All users",
      panelID: "all-users-content",
      value: "all",
    },
    {
      id: "seller",
      content: cms("label.tab.seller"),
      panelID: "seller-content",
      value: "seller",
    },
    {
      id: "vendor",
      content: cms("label.tab.vendor"),
      panelID: "vendor-content",
      value: "vendor",
    },
  ];

  useEffect(() => {
    const filterApplied = baseHelper.queryParamsFromLocation(location);
    setDataToFetch({ ...dataToFetch, filterApplied });
  }, [location.search]);

  const handleTabChange = useCallback(
    (selectedTabIndex) => {
      const { value: tabName } = tabs[selectedTabIndex];
      setTabSelected(selectedTabIndex);
      baseHelper.setUrl(location, navigate, { tab: tabName, tabKey: selectedTabIndex, filter: tabName });
      const filteredObj = baseHelper.convertToInt(baseHelper.queryParamsFromLocation(location), {
        perPage: 10,
        page: 1,
      });
      setDataToFetch({ ...filteredObj, page: parseInt(currentPage, 10) });
    },
    [currentPage, location, tabs]
  );

  const handlePerPage = (page) => {
    setCurrentPage(parseInt(page, 10));
    baseHelper.setUrl(location, navigate, { page });
    const filteredObj = baseHelper.convertToInt(baseHelper.queryParamsFromLocation(location), { perPage: 10, page: 1 });
    setDataToFetch({ ...filteredObj, page: parseInt(page, 10) });
  };

  const handleSelectLimit = useCallback(
    (value) => {
      setSelectedLimit(parseInt(value, 10));
      baseHelper.setUrl(location, navigate, { perPage: parseInt(value, 10) });
    },
    [currentPage, location, navigate]
  );

  const renderItem = (item) => {
    const notFound = "Not Found";
    const { _id, login, logout, brandName = notFound, ip = notFound, device = notFound, userId } = item;
    const loginDateTime = baseHelper.formatDate(login) || notFound;
    const logoutDateTime = baseHelper.formatDate(logout) || notFound;
    const loggedInTime = baseHelper.formatDate(login) || notFound;

    return (
      <>
        <ResourceItem id={_id}>
          <BlockStack>
            <InlineStack align="start">
              <Link url={`${constant.PROVIDER_VIEW}${userId}`}>{brandName || notFound}</Link>
              <Text as="span" fontWeight="semibold">{`${cms("label.logInTime")}:`}</Text>
              {` ${loginDateTime}`}
              <Text as="span" fontWeight="semibold">{`${cms("label.logOutTime")}:`}</Text>
              {` ${logoutDateTime}`}
              <Text as="span" fontWeight="semibold">{`${cms("label.ip")}:`}</Text>
              {` ${ip || notFound}`}
            </InlineStack>
            <InlineStack align="start">
              <Text as="span" fontWeight="semibold">{`${cms("label.loggedInTime")}:`}</Text>
              {` ${loggedInTime}`}
              <Text as="span" fontWeight="semibold">{`${cms("label.device")}:`}</Text>
              {` ${device}`}
            </InlineStack>
          </BlockStack>
        </ResourceItem>
      </>
    );
  };

  const handleSearchChange = (searchValue) => {
    setSearch(searchValue);
    baseHelper.setUrl(location, navigate, { search: searchValue, page: 1 });
  };

  const handleQueryValueRemove = () => {
    setSearch("");
  };

  function isEmpty(value) {
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    return value === "" || value == null;
  }

  // function for remove values of more filter.
  const handleTaggedWithRemove = () => {
    setTaggedWith(null);
    baseHelper.setUrl(location, navigate, { list_search: "" });
  };

  const filterOptions = [
    { label: cms("label.filterOption.select"), value: "" },
    { label: cms("label.filterOption.user"), value: "brandName" },
  ];

  function disambiguateLabel(key, value) {
    switch (key) {
      case "userFilter":
        return `${value}`;
      default:
        return value;
    }
  }

  const appliedFilterArrObj = [
    {
      key: "userFilter",
      label: disambiguateLabel("userFilter", taggedWith),
      onRemove: handleTaggedWithRemove,
    },
  ];
  const appliedFilters = !isEmpty(taggedWith) ? appliedFilterArrObj : [];

  const handleSelectChange = (value) => {
    setSelectedFilter(value);
    baseHelper.setUrl(location, navigate, { list_filter: value });
  };

  const handleTaggedWithChange = (value) => {
    setTaggedWith(value);
    baseHelper.setUrl(location, navigate, { list_search: value });
  };

  const customFilter = (
    <FormLayout>
      <Select options={filterOptions} value={selectedFilter} placeholder="Filter" onChange={handleSelectChange} />
      {(selectedFilter && <TextField label="like" value={taggedWith} onChange={handleTaggedWithChange} />) || null}
    </FormLayout>
  );

  const filters = [
    {
      key: cms("label.filterBy"),
      label: cms("label.filterBy"),
      filter: customFilter,
      shortcut: false,
    },
  ];

  return (
    <>
      {banner.isOpen && (
        <Layout.Section>
          <Banner
            isOpen={banner.isOpen}
            status={banner.status}
            title={banner.title}
            onDismiss={() => setBanner(() => setBanner({ isOpen: false, title: "", status: "" }))}
          />
        </Layout.Section>
      )}
      <Layout.Section>
        <Card>
          <Tabs tabs={tabs} selected={tabSelected} onSelect={handleTabChange}>
            <ResourceList
              resourceName={cms("label.resourceName")}
              renderItem={renderItem}
              items={userList || []}
              loading={loading}
              onQueryChange={handleSearchChange}
              onQueryClear={handleQueryValueRemove}
              queryValue={search}
              filters={filters}
              appliedFilters={appliedFilters}
            />
          </Tabs>
          <SessionFooter
            count={totalUSerList}
            optionList={options}
            perPageCount={selectedLimit}
            onSelectLimit={handleSelectLimit}
            onPerPage={handlePerPage}
            currentPage={currentPage}
            isLoading={loading}
          />
        </Card>
      </Layout.Section>
    </>
  );
};

export default withErrorBoundary(AdminSessionList);
